// Step-up (re-verify identity before high-privilege actions) is wired to
// Clerk's reverification flow. `useReverification` wraps an arbitrary
// fetcher: when the fetcher returns Clerk's standard reverification "hint"
// object, the hook opens Clerk's hosted reverification modal, waits for it to
// complete, and re-invokes the fetcher exactly once. On modal cancellation it
// throws a `ReverificationCancelledError`.
//
// Server side: a route guarded by `withStepUp()` returns 401 with code
// `admin/requires_step_up` when the Clerk session is outside its step-up
// window (see `identity/service/load-session.ts`). The fetcher here adapts
// that platform error code into the shape `useReverification` watches for.

import { useCallback } from 'react';
import { useReverification } from '@clerk/clerk-react';
import { isReverificationCancelledError } from '@clerk/clerk-react/errors';

import { apiErrorCode } from './api-error';

const STEP_UP_ERROR_CODE = 'admin/requires_step_up';

// Clerk's reverification "hint" — the exact object shape `useReverification`
// watches for in fetcher return values. Importing the runtime helper from
// `@clerk/shared` would couple us to a non-public surface; the shape itself
// is part of Clerk's documented public contract (see
// clerk.com/docs/guides/secure/reverification).
const REVERIFICATION_HINT = {
  clerk_error: {
    type: 'forbidden' as const,
    reason: 'reverification-error' as const,
  },
} as const;

type ReverificationHint = typeof REVERIFICATION_HINT;

// A step-up rejection, returned to `useReverification` as Clerk's hint so it
// opens the modal. It also keeps the server's error: when the retry after a
// completed reverification is still rejected, Clerk hands this object back
// unchanged and it surfaces as an ordinary error outcome.
type StepUpRequired = ReverificationHint & { readonly error: unknown };

export type StepUpAttempt<T> =
  | { readonly ok: true; readonly data: T }
  | { readonly ok: false; readonly error: unknown; readonly code: string }
  | StepUpRequired;

// Outcome of running a mutation through step-up. `cancelled` is the member
// dismissing Clerk's reverification modal: the caller leaves state untouched.
// `error` carries the thrown failure and its machine code — including a retry
// the server still rejects as `admin/requires_step_up` after reverification —
// and the caller renders `error` through `useErrorMessage` so the copy is
// localized. `ok` carries the mutation's own return value.
export type StepUpOutcome<T> =
  | { readonly status: 'ok'; readonly data: T }
  | { readonly status: 'cancelled' }
  | { readonly status: 'error'; readonly error: unknown; readonly code: string };

export async function attemptStepUpMutation<T>(
  mutate: () => Promise<T>,
): Promise<StepUpAttempt<T>> {
  try {
    return { ok: true, data: await mutate() };
  } catch (error) {
    const code = apiErrorCode(error) ?? 'UNKNOWN';
    if (code === STEP_UP_ERROR_CODE) return { ...REVERIFICATION_HINT, error };
    return { ok: false, error, code };
  }
}

export function stepUpOutcome<T>(attempt: StepUpAttempt<T>): StepUpOutcome<T> {
  if ('clerk_error' in attempt) {
    return { status: 'error', error: attempt.error, code: STEP_UP_ERROR_CODE };
  }
  if (attempt.ok) return { status: 'ok', data: attempt.data };
  return { status: 'error', error: attempt.error, code: attempt.code };
}

export interface UseStepUpResult {
  // Run a mutation, auto-trigger reverification when the server demands
  // step-up, and normalise every result into a `StepUpOutcome` the caller
  // switches on.
  readonly runStepUp: <T>(mutate: () => Promise<T>) => Promise<StepUpOutcome<T>>;
}

export function useStepUp(): UseStepUpResult {
  const enhanced = useReverification(attemptStepUpMutation);

  const runStepUp = useCallback(
    async <T>(mutate: () => Promise<T>): Promise<StepUpOutcome<T>> => {
      let attempt: StepUpAttempt<T>;
      try {
        // Clerk's typing strips the hint from the fetcher's return type, but
        // a retry that is still rejected returns it unchanged.
        attempt = (await enhanced(mutate)) as StepUpAttempt<T>;
      } catch (e) {
        if (isReverificationCancelledError(e)) return { status: 'cancelled' };
        throw e;
      }
      return stepUpOutcome(attempt);
    },
    [enhanced],
  );

  return { runStepUp };
}
